import type { components } from '@/api'
import { nonNullable } from '@/shared/utils/typeAssertions'
import { computed, type ComputedRef, type Ref } from 'vue'
import type { Claim, Source } from '../useGroundingStore'
import { useSerializeFieldToText } from '../useSerializeFieldToText'
import type { GroundableField } from './types'
import { getClaimStartAndEnd } from './utils/grounding'

type BackendGrounding = components['schemas']['Projects.Grounding.Grounding']
type BackendSource = BackendGrounding['sources'][number]
type BoundingBox = BackendSource['bounding_boxes'][number]

export const useFieldClaimsAndSources = (
  field: Ref<GroundableField | null>,
): ComputedRef<{ claims: Claim[]; sources: Source[] }> => {
  const serializeFieldToText = useSerializeFieldToText()
  return computed(() => {
    if (!field.value?.grounding) return { claims: [], sources: [] }

    const referencedSourceIds = field.value.grounding.claims.reduce((acc, claim) => {
      claim.source_indices.forEach(acc.add, acc)
      return acc
    }, new Set<number>())

    const compareBoundingBoxes = (a: BoundingBox, b: BoundingBox) =>
      a.page - b.page || a.ymin - b.ymin || a.xmin - b.xmin

    const backendSources = field.value.grounding.sources
      .map((s) => ({ ...s, bounding_boxes: s.bounding_boxes.toSorted(compareBoundingBoxes) }))
      .filter((s) => s.bounding_boxes.length && referencedSourceIds.has(s.index))
      .toSorted((a, b) => {
        return (
          a.property_id.localeCompare(b.property_id) ||
          compareBoundingBoxes(nonNullable(a.bounding_boxes[0]), nonNullable(b.bounding_boxes[0]))
        )
      })

    const claims = field.value.grounding.claims
      .toSorted((a, b) => a.location.offset - b.location.offset)
      .reduce<{ acc: Claim[]; lastEnd: number }>(
        ({ acc, lastEnd }, claim) => {
          if (!field.value) {
            return { acc, lastEnd }
          }

          const adjustedStartEnd = getClaimStartAndEnd(
            claim,
            field.value,
            serializeFieldToText(field.value),
            lastEnd,
          )
          if (!adjustedStartEnd) {
            return { acc, lastEnd }
          }

          const { start, end } = adjustedStartEnd

          const sources = claim.source_indices
            .map((source_index) => backendSources.findIndex(({ index }) => index === source_index))
            .filter((idx) => idx !== -1)
            .toSorted()

          if (sources.length) {
            acc.push({ id: -1, start, end, sources })
          }
          return { acc, lastEnd: end }
        },
        { acc: [], lastEnd: 0 },
      )
      .acc.map((claim, idx) => ({ ...claim, id: idx }))

    const sources: Source[] = backendSources.map((source, idx) => ({
      id: idx,
      propertyId: source.property_id,
      boundingBoxes: source.bounding_boxes,
    }))

    return { sources, claims }
  })
}
